import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

type Variant = "navbar" | "mobile";

const LANGS = [
  { code: "en", label: "English", short: "EN", flag: "🇬🇧" },
  { code: "el", label: "Ελληνικά", short: "ΕΛ", flag: "🇬🇷" },
] as const;

const LanguageSwitcher = ({ variant = "navbar" }: { variant?: Variant }) => {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  const active = LANGS.find((l) => l.code === current) ?? LANGS[0];

  const change = (code: string) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem("stl_lang", code);
    } catch {
      /* ignore */
    }
  };

  if (variant === "mobile") {
    return (
      <div className="flex items-center gap-2">
        {LANGS.map((l) => {
          const isActive = l.code === current;
          return (
            <button
              key={l.code}
              type="button"
              onClick={() => change(l.code)}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-xs font-bold uppercase tracking-[0.15em] transition-all",
                isActive
                  ? "bg-secondary text-black border-secondary"
                  : "bg-white/5 text-white border-white/10 hover:bg-white/10"
              )}
              aria-pressed={isActive}
            >
              <span className="text-base leading-none">{l.flag}</span>
              <span>{l.short}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Change language"
          className="flex items-center gap-1.5 px-3 h-9 rounded-full border border-ocean-deep/15 bg-white/70 hover:bg-ocean-deep/5 text-ocean-deep transition-all text-xs font-bold uppercase tracking-widest"
        >
          <Globe className="h-3.5 w-3.5" />
          <span>{active.short}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {LANGS.map((l) => {
          const isActive = l.code === current;
          return (
            <DropdownMenuItem
              key={l.code}
              onClick={() => change(l.code)}
              className="flex items-center justify-between gap-2 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <span className="text-base leading-none">{l.flag}</span>
                <span className="font-body text-sm">{l.label}</span>
              </span>
              {isActive && <Check className="h-4 w-4 text-secondary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
